/**
 * Patient Visit tab – patient card + OPD consultation form + mic.
 * Session strip (Start / End Visit) lives in WorkflowSessionBar under main tabs.
 */
import { Box, Paper, Typography, FormControlLabel, Switch } from '@mui/material';
import { Person } from '@mui/icons-material';
import { useSession } from '../../contexts/SessionContext';
import { useUser } from '../../contexts/UserContext';
import { useConfidenceToggle } from '../../contexts/ConfidenceToggleContext';
import { NewOPDForm } from '../NewOPDForm';
import RightPanelTabs from '../RightPanel/RightPanelTabs';
import { RIGHT_PANEL_WIDTH, RIGHT_PANEL_WIDTH_XS } from '../RightPanel/constants';
import AmbientMicButton from '../AmbientMicButton/AmbientMicButton';
import WorkflowSessionBar from '../Workflow/WorkflowSessionBar';

export default function Consultation() {
  const { session, formSessionOverride } = useSession();
  const { patient } = useUser();
  const { showConfidence, setShowConfidence } = useConfidenceToggle();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        p: 2,
        minHeight: 0,
        overflow: 'hidden',
        bgcolor: '#f5f5f5',
      }}
    >
      <Box
        sx={{
          // flex: { md: '1 1 65%' },          
          flex: { xs: '1 1 0%', md: '1 1 65%' },
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        {/* Single scroll container so session bar + patient card scroll together (mobile consistency). */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative', overflowX: 'hidden' }}>
          <Box sx={{ flexShrink: 0, mb: 1 }}>
            <WorkflowSessionBar />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A4D9A', fontFamily: 'inherit' }}>
                Patient Visit
              </Typography>
              <Typography variant="body2" sx={{ color: '#757575', mt: 0.5, fontFamily: 'inherit' }}>
                Complete clinical assessment and documentation
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ bgcolor: '#eef3fb', borderRadius: 20, px: 1.5, py: 0.2 }}>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showConfidence}
                      onChange={(_, v) => setShowConfidence(v)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#008AD2' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#008AD2' },
                      }}
                    />
                  }
                  label="AI Confidence"
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.85rem', color: '#4a4a4a', fontFamily: 'inherit' } }}
                />
              </Box>
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: '6px',
                  border: '1px solid #8DBAEA',
                  borderColor: '#8DBAEA',
                  px: 2,
                  py: 1.25,
                  bgcolor: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Person sx={{ color: '#1A4D9A', fontSize: 22 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#666666', display: 'block', fontFamily: 'inherit' }}>
                    Patient
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#333333', fontFamily: 'inherit' }}>
                    {patient?.name || 'No patient selected'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#757575', fontFamily: 'inherit' }}>
                    MRN: {patient?.mrn || '–'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>

          {(!!formSessionOverride || session?.status === 'active' || patient) && (
            <>
              {formSessionOverride || session?.status === 'active' ? (
                <>
                  <NewOPDForm sessionId={(formSessionOverride ?? session)!.session_id} consultationOnly />
                  <AmbientMicButton placement="fixed" />
                </>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Start a session (bar above) to fill the Patient Visit form and use voice capture.
                  </Typography>
                </Paper>
              )}
            </>
          )}

          {!patient && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Go to Dashboard and click &quot;Open&quot; on a patient or &quot;Start Consultation&quot; on an appointment to begin.
              </Typography>
            </Paper>
          )}
        </Box>
      </Box>
      {/* <Box sx={{ width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH }, flexShrink: 0, minHeight: 0, display: 'flex' }}></Box> */}
      <Box
        sx={{
          width: { xs: RIGHT_PANEL_WIDTH_XS, md: RIGHT_PANEL_WIDTH },
          flexShrink: 0,
          flex: { xs: '1 1 0%', md: '0 0 auto' },
          minHeight: 0,
          height: '100%',
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        <RightPanelTabs
          showCurrentVisitContent={!!session && session.status === 'active'}
          defaultTab="currentVisit"
          sessionId={session?.session_id ?? null}
        />
      </Box>
    </Box>
  );
}
